import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BasicButton from '../../../components/button/BasicButton';
import AdMarkDialog from '../../../components/dialog/AdMarkDialog';
import ProgressBar from '../../../components/common/ProgressBar';
import ErrorState from '../../../components/common/ErrorStats';
import NoData from '../../../components/common/NoData';
import { adminApi } from '../../../api/admin';
import theme from '../../../theme';

const HEAD_CELLS = [
  { id: 'artistName', label: '아티스트' },
  { id: 'albumTitle', label: '앨범' },
  { id: 'genre', label: '장르' },
  { id: 'releaseDate', label: '발매일' },
  { id: 'rating', label: '평점' },
  { id: 'reviewCount', label: '리뷰수' },
  { id: 'isAdMarked', label: '광고 마크' },
  { id: 'displayRegion', label: '분류' },
];

const getAlbumUrl = (album) =>
  album.slug
    ? `/reviews/${album.slug}`
    : `/reviews/${album.albumSource?.toLowerCase()}/${album.sourceId}`;

const SectionTitle = ({ title }) => {
  const [leftTitle, rightTitle] = title.split('|').map((part) => part.trim());
  const hasDivider = Boolean(rightTitle);

  if (!hasDivider) {
    return (
      <Typography
        fontSize={{ xs: 18, md: 20, lg: 24 }}
        fontWeight="bold"
        color={theme.palette.black[9]}
        sx={{ mb: { xs: '20px', md: '30px' } }}
      >
        {title}
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={{ xs: 1, md: 1.25 }}
      sx={{ mb: { xs: '20px', md: '30px' } }}
    >
      <Typography
        component="span"
        fontSize={{ xs: 18, md: 20, lg: 24 }}
        fontWeight="bold"
        color={theme.palette.black[9]}
        lineHeight={1.2}
      >
        {leftTitle}
      </Typography>
      <Box
        sx={{
          width: '1px',
          height: { xs: 12, md: 14, lg: 16 },
          backgroundColor: theme.palette.black[3],
        }}
      />
      <Typography
        component="span"
        fontSize={{ xs: 18, md: 20, lg: 24 }}
        fontWeight="bold"
        color={theme.palette.black[9]}
        lineHeight={1.2}
      >
        {rightTitle}
      </Typography>
    </Stack>
  );
};

const SortableAlbumRow = ({ album, cellStyle, textStyle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: album.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes} hover>
      <TableCell sx={{ ...cellStyle, cursor: 'grab', p: 0, touchAction: 'none' }} {...listeners}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <DragIndicatorIcon sx={{ color: theme.palette.black[3] }} />
        </Box>
      </TableCell>
      <AlbumCells album={album} cellStyle={cellStyle} textStyle={textStyle} />
    </TableRow>
  );
};

const AlbumCells = ({ album, cellStyle, textStyle }) => {
  const artist = album.artist || {};
  const artistPath =
    artist.sourceId && artist.artistSource
      ? `/artists/${artist.artistSource.toLowerCase()}/${artist.sourceId}`
      : null;

  return (
    <>
      <TableCell sx={{ ...cellStyle, minWidth: 180 }}>
        {artistPath ? (
          <Link component={RouterLink} to={artistPath} sx={{ textDecoration: 'none' }}>
            <Typography {...textStyle}>{artist.name || '-'}</Typography>
          </Link>
        ) : (
          <Typography {...textStyle}>{artist.name || '-'}</Typography>
        )}
      </TableCell>
      <TableCell sx={{ ...cellStyle, minWidth: 300 }}>
        <Link component={RouterLink} to={getAlbumUrl(album)} sx={{ textDecoration: 'none' }}>
          <Typography {...textStyle}>{album.title}</Typography>
        </Link>
      </TableCell>
      <TableCell sx={{ ...cellStyle, minWidth: 180 }}>
        <Typography {...textStyle}>{album.genres?.join(', ') || '-'}</Typography>
      </TableCell>
      <TableCell sx={cellStyle}>
        <Typography {...textStyle}>{album.releaseDate || '-'}</Typography>
      </TableCell>
      <TableCell align="right" sx={cellStyle}>
        <Typography {...textStyle}>{Number(album.averageRating || 0).toFixed(1)}</Typography>
      </TableCell>
      <TableCell align="right" sx={cellStyle}>
        <Typography {...textStyle}>{Number(album.reviewCount || 0).toLocaleString()}</Typography>
      </TableCell>
      <TableCell align="center" sx={cellStyle}>
        <Typography {...textStyle}>{album.isAdMarked ? 'Y' : 'N'}</Typography>
      </TableCell>
      <TableCell align="center">
        <Typography {...textStyle}>{album.displayRegion || '-'}</Typography>
      </TableCell>
    </>
  );
};

const AlbumSection = ({
  title,
  section,
  albums,
  onOrderUpdate,
  onAdMarkUpdate,
  cellStyle,
  textStyle,
  buttonStyle,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [orderedAlbums, setOrderedAlbums] = useState([]);
  const [isAdMarkDialogOpen, setIsAdMarkDialogOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const displayAlbums = isEditingOrder ? orderedAlbums : albums;
  const numSelected = albums.filter((album) => selectedIds.includes(album.id)).length;
  const isAllSelected = albums.length > 0 && numSelected === albums.length;
  const isIndeterminate = numSelected > 0 && !isAllSelected;
  const hasSelection = selectedIds.length > 0;
  const headCells = HEAD_CELLS;

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedIds(albums.map((album) => album.id));
      return;
    }
    setSelectedIds([]);
  };

  const handleSelectClick = (event, id) => {
    event.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((albumId) => albumId !== id) : [...prev, id],
    );
  };

  const handleStartEditOrder = () => {
    setOrderedAlbums([...albums]);
    setIsEditingOrder(true);
  };

  const handleCancelEditOrder = () => {
    setOrderedAlbums([]);
    setIsEditingOrder(false);
  };

  const handleSaveOrder = async () => {
    await onOrderUpdate(
      section,
      orderedAlbums.map((album) => album.id),
    );
    setSelectedIds([]);
    setIsEditingOrder(false);
  };

  const handleAdMarkUpdate = async (isAdMarked) => {
    await onAdMarkUpdate(
      section,
      selectedIds,
      albums.map((album) => album.id),
      isAdMarked,
    );
    setSelectedIds([]);
    setIsAdMarkDialogOpen(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setOrderedAlbums((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  return (
    <Box>
      <SectionTitle title={title} />

      <Stack direction="row" spacing={1} sx={{ mb: { xs: '20px', md: '16px' } }}>
        {isEditingOrder ? (
          <>
            <BasicButton variant="outlined" onClick={handleSaveOrder} sx={buttonStyle}>
              저장
            </BasicButton>
            <BasicButton variant="outlined" onClick={handleCancelEditOrder} sx={buttonStyle}>
              취소
            </BasicButton>
          </>
        ) : (
          <>
            <BasicButton
              variant="outlined"
              onClick={handleStartEditOrder}
              disabled={albums.length < 2}
              sx={buttonStyle}
            >
              순서 변경
            </BasicButton>
            <BasicButton
              variant="outlined"
              onClick={() => setIsAdMarkDialogOpen(true)}
              disabled={!hasSelection}
              sx={buttonStyle}
            >
              광고 마크 관리
            </BasicButton>
          </>
        )}
      </Stack>

      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey.bg }}>
                <TableCell padding="checkbox" sx={cellStyle}>
                  {isEditingOrder ? (
                    <Box sx={{ width: 42, height: 42 }} />
                  ) : (
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={handleSelectAllClick}
                      size="small"
                      disabled={albums.length === 0}
                      sx={{
                        color: theme.palette.grey.line,
                        '&.Mui-checked': { color: '#f16438' },
                        '&.MuiCheckbox-indeterminate': { color: '#f16438' },
                      }}
                    />
                  )}
                </TableCell>
                {headCells.map((headCell, index) => (
                  <TableCell
                    key={headCell.id}
                    align="center"
                    sx={{
                      whiteSpace: 'nowrap',
                      borderRight:
                        index === headCells.length - 1
                          ? 'none'
                          : `1px solid ${theme.palette.grey.line}`,
                    }}
                  >
                    <Typography fontSize={14} color={theme.palette.black[4]}>
                      {headCell.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isEditingOrder ? (
                <SortableContext
                  items={orderedAlbums.map((album) => album.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedAlbums.map((album) => (
                    <SortableAlbumRow
                      key={album.id}
                      album={album}
                      cellStyle={cellStyle}
                      textStyle={textStyle}
                    />
                  ))}
                </SortableContext>
              ) : (
                displayAlbums.map((album) => (
                  <TableRow key={album.id} hover>
                    <TableCell padding="checkbox" sx={cellStyle}>
                      <Checkbox
                        checked={selectedIds.includes(album.id)}
                        onClick={(event) => handleSelectClick(event, album.id)}
                        size="small"
                        sx={{
                          color: theme.palette.grey.line,
                          '&.Mui-checked': { color: '#f16438' },
                        }}
                      />
                    </TableCell>
                    <AlbumCells album={album} cellStyle={cellStyle} textStyle={textStyle} />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DndContext>
      </TableContainer>
      {albums.length === 0 && <NoData message="등록된 앨범이 없습니다" />}
      <AdMarkDialog
        open={isAdMarkDialogOpen}
        onClose={() => setIsAdMarkDialogOpen(false)}
        onConfirm={handleAdMarkUpdate}
        defaultAdMarked={
          !(
            selectedIds.length > 0 &&
            albums
              .filter((album) => selectedIds.includes(album.id))
              .every((album) => !album.isAdMarked)
          )
        }
      />
    </Box>
  );
};

const HomeReviewManagement = ({ onCountsChange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [koreanAlbums, setKoreanAlbums] = useState([]);
  const [globalAlbums, setGlobalAlbums] = useState([]);
  const [recommendedAlbums, setRecommendedAlbums] = useState([]);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApi.getHomeAlbums();
        setKoreanAlbums(response.data.koreanNewReleaseAlbums || []);
        setGlobalAlbums(response.data.globalNewReleaseAlbums || []);
        setRecommendedAlbums(response.data.recommendedAlbums || []);
      } catch (err) {
        setError(err);
        setKoreanAlbums([]);
        setGlobalAlbums([]);
        setRecommendedAlbums([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refetchTrigger]);

  const handleOrderUpdate = async (section, albumIds) => {
    await adminApi.updateHomeAlbumOrder(section, albumIds);
    setRefetchTrigger((c) => c + 1);
    onCountsChange?.();
  };

  const handleAdMarkUpdate = async (section, albumIds, visibleAlbumIds, isAdMarked) => {
    await adminApi.updateHomeAlbumAdMark(section, albumIds, visibleAlbumIds, isAdMarked);
    setRefetchTrigger((c) => c + 1);
    onCountsChange?.();
  };

  const cellStyle = useMemo(() => ({ borderRight: `1px solid ${theme.palette.grey.line}` }), []);
  const textStyle = useMemo(() => ({ fontSize: 14, color: theme.palette.black[9] }), []);
  const buttonStyle = useMemo(
    () => ({ typography: 'body3', fontWeight: 'medium', padding: '12px 16px' }),
    [],
  );

  return (
    <ProgressBar isLoading={loading}>
      {error ? (
        <ErrorState />
      ) : (
        <Stack spacing={{ xs: '30px', md: '50px', lg: '60px' }}>
          <AlbumSection
            title="최신 앨범 | 국내"
            section="korean"
            albums={koreanAlbums}
            onOrderUpdate={handleOrderUpdate}
            onAdMarkUpdate={handleAdMarkUpdate}
            cellStyle={cellStyle}
            textStyle={textStyle}
            buttonStyle={buttonStyle}
          />
          <AlbumSection
            title="최신 앨범 | 해외"
            section="global"
            albums={globalAlbums}
            onOrderUpdate={handleOrderUpdate}
            onAdMarkUpdate={handleAdMarkUpdate}
            cellStyle={cellStyle}
            textStyle={textStyle}
            buttonStyle={buttonStyle}
          />
          <AlbumSection
            title="포스트팝 추천"
            section="recommended"
            albums={recommendedAlbums}
            onOrderUpdate={handleOrderUpdate}
            onAdMarkUpdate={handleAdMarkUpdate}
            cellStyle={cellStyle}
            textStyle={textStyle}
            buttonStyle={buttonStyle}
          />
        </Stack>
      )}
    </ProgressBar>
  );
};

export default HomeReviewManagement;
